import React from "react"

import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import * as Models from "@/redux/models"
import { useRootSelector } from "@/redux/useRootSelector"
import type { AcceptsChildren } from "@/theme"
import { AlertBodyText, AlertCard, Box, Button, Row } from "@/theme"
import i18n from "@/translations/i18n"
import { useRouter } from "expo-router"
// TODO: Extract Prompt for Device land screen
export function isTravelerPathSet(
  configuration: DeviceConfiguration | null | undefined,
): boolean {
  if (configuration) {
    if (configuration.deviceInstallationType === "linear_move") {
      return (
        Boolean(configuration.linearPath) &&
        Boolean(configuration.linearPathStopsLabels) &&
        typeof configuration.linearSpanWidthMm === "number"
      )
    }
    // Pivot geometry lives on CenterPivot (migration 0266); the dedicated
    // pivot builder flow handles pivot-path setup.
  }
  return true
}

export function TravelerPathNotSetAlert({
  children,
  text,
}: AcceptsChildren & { text: string }) {
  return (
    <AlertCard my={"$2"} severity={"warning"}>
      <Row>
        <Box mr={"$2"}>
          <AlertBodyText flex={1}>{text}</AlertBodyText>
        </Box>
        {children}
      </Row>
    </AlertCard>
  )
}

export function SetTravellerPathPrompt({
  deviceId,
  hideButtonIfPathIsSet,
}: Models.DeviceIdProps & {
  hideButtonIfPathIsSet: boolean
}) {
  const withPermissions = usePermissionCheckedAction()
  const router = useRouter()
  const configuration = useRootSelector((state) =>
    Models.deviceConfiguration.selectById(state, deviceId),
  )

  if (!configuration || !isTravelerPathSet(configuration)) {
    return null
  }
  const { codaDeviceAlias } = configuration

  let element: React.JSX.Element | null = null
  switch (configuration.deviceInstallationType) {
    case "linear_move": {
      element = (
        <Button
          IconComponent={"DeviceLinearMove"}
          id={"setLinearPathLink"}
          text={i18n.t("setLinearPath:linkTo")}
          onPress={withPermissions({
            callback: () => {
              router.navigate({
                params: { codaDeviceAlias },
                pathname: "/devices/[codaDeviceAlias]/set-linear-path",
              })
            },
            required: "canManageDeviceConfiguration",
          })}
        />
      )

      if (!isTravelerPathSet(configuration)) {
        // Path is not set
        element = (
          <TravelerPathNotSetAlert
            text={i18n.t("setLinearPath:pathNotSetWarning")}
          >
            {element}
          </TravelerPathNotSetAlert>
        )
      } else if (hideButtonIfPathIsSet) {
        element = null
      }
      break
    }
    // Legacy `center_pivot` installation type retired in migration 0264; the
    // pivot builder UI owns pivot geometry for monitor/controller devices.

    default:
      break
  }
  return element
}
